/**
 * DocumentTypes Screen
 *
 * Maps raw document types found in the uploaded data (e.g., "Invoice",
 * "Credit Memo", "Debit Memo Reversal") to the five rollforward activity
 * categories: Sales, Collection, Dilution, DMR Collection, DMR Dilution.
 *
 * Each document type produces two pills -- positive (+) and negative (-) --
 * representing the debit and credit sides. The user drag-and-drops each pill
 * into the appropriate category pad. This mapping ("normMap") is critical
 * for the Rollforward calculation, which uses it to classify every invoice
 * movement into the correct activity bucket.
 *
 * Data flow:
 *  - `getDocumentTypes` fetches distinct document types from the master frame.
 *  - `normMap` is stored in project state and explicitly saved on every change.
 *  - Pill placement state is derived from normMap, not stored separately.
 */
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getDocumentTypes, getDocTypeSums, predictCategories } from "Redux/data/actions";
import { saveProject } from "Redux/project/actions";
import TemplateManager from "Components/TemplateManager";

const CATEGORIES = ["Sales", "Collection", "Dilution", "DMR Collection", "DMR Dilution", "Write-Off"];

const CATEGORY_COLORS = {
  Sales: { bg: "#430098", color: "#fff" },
  Collection: { bg: "#3DB887", color: "#fff" },
  Dilution: { bg: "#D64545", color: "#fff" },
  "DMR Collection": { bg: "#79589F", color: "#fff" },
  "DMR Dilution": { bg: "#E8A735", color: "#fff" },
  "Write-Off": { bg: "#6C63FF", color: "#fff" },
};

// Draggable pill representing one side (+/-) of a document type.
// Once placed in a category, the pill is grayed out and non-draggable.
const Pill = ({ label, pillKey, isPlaced, isPositive, docType, onDragStart }) => (
  <span
    draggable={!isPlaced}
    onDragStart={(e) => {
      if (!isPlaced) {
        e.dataTransfer.setData("pillKey", pillKey);
        onDragStart(pillKey);
      }
    }}
    className="tag is-medium"
    style={{
      cursor: isPlaced ? "not-allowed" : "grab",
      opacity: isPlaced ? 0.25 : 1,
      fontWeight: 700,
      borderRadius: "6px",
      fontSize: "0.75rem",
      userSelect: "none",
      marginRight: "0.35rem",
      marginBottom: "0.35rem",
      backgroundColor: "var(--ev-white)",
      color: "var(--ev-text)",
      border: "1px solid var(--ev-border)",
      display: "inline-flex",
      alignItems: "center",
      gap: "0.3rem",
      padding: "0.35em 0.65em",
    }}
  >
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "16px",
        height: "16px",
        borderRadius: "4px",
        fontSize: "0.7rem",
        fontWeight: 800,
        backgroundColor: isPositive ? "#3DB887" : "#D64545",
        color: "#fff",
        lineHeight: 1,
      }}
    >
      {isPositive ? "+" : "-"}
    </span>
    {docType}
  </span>
);

// Drop target for a rollforward category. Shows placed pills with
// delete buttons. Visual feedback (border/background) on drag-over.
const CategoryPad = ({ category, pills, onDrop, onRemove }) => {
  const [dragOver, setDragOver] = useState(false);
  const catStyle = CATEGORY_COLORS[category] || { bg: "#ccc", color: "#333" };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const pillKey = e.dataTransfer.getData("pillKey");
        if (pillKey) onDrop(category, pillKey);
      }}
      style={{
        flex: "1 1 0",
        minWidth: "180px",
        minHeight: "120px",
        borderRadius: "var(--ev-radius-lg)",
        border: dragOver
          ? "2px solid var(--ev-purple)"
          : "2px dashed var(--ev-border)",
        backgroundColor: dragOver ? "var(--ev-purple-ghost)" : "var(--ev-white)",
        padding: "0.75rem",
        transition: "all 0.15s ease",
      }}
    >
      <div style={{ marginBottom: "0.5rem" }}>
        <span
          className="tag"
          style={{
            backgroundColor: catStyle.bg,
            color: catStyle.color,
            fontWeight: 700,
            fontSize: "0.72rem",
            borderRadius: "6px",
            letterSpacing: "0.02em",
          }}
        >
          {category}
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
        {pills.map((pill) => {
          const isPos = pill.key.startsWith("p");
          return (
            <span
              key={pill.key}
              className="tag"
              style={{
                fontWeight: 600,
                fontSize: "0.72rem",
                borderRadius: "6px",
                backgroundColor: "var(--ev-white)",
                color: "var(--ev-text)",
                border: "1px solid var(--ev-border)",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.25rem",
                padding: "0.3em 0.55em",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "14px",
                  height: "14px",
                  borderRadius: "3px",
                  fontSize: "0.65rem",
                  fontWeight: 800,
                  backgroundColor: isPos ? "#3DB887" : "#D64545",
                  color: "#fff",
                  lineHeight: 1,
                }}
              >
                {isPos ? "+" : "-"}
              </span>
              {pill.docType}
              <button
                className="delete is-small"
                onClick={() => onRemove(pill.key)}
                style={{ marginLeft: "0.15rem" }}
              />
            </span>
          );
        })}
        {pills.length === 0 && (
          <span
            style={{
              fontSize: "0.7rem",
              color: "var(--ev-text-muted)",
              fontStyle: "italic",
            }}
          >
            Drop pills here
          </span>
        )}
      </div>
    </div>
  );
};

// --------------------------------------------------------------
// --------------------------------------------------------------
const DocumentTypes = () => {
  const [normMap, setNormMapLocal] = useState({});
  const [predicting, setPredicting] = useState(false);
  const { documentTypes, predictedNormMap } = useSelector((state) => state.data);
  const { hasMaster, normMap: savedNormMap, docTypeSums, docTypeDescriptions } = useSelector(
    (state) => state.project
  );
  const [summaryTab, setSummaryTab] = useState("amounts");
  const dispatch = useDispatch();

  useEffect(() => {
    if (savedNormMap) {
      setNormMapLocal(savedNormMap);
    }
  }, [savedNormMap]);

  useEffect(() => {
    if (hasMaster && !documentTypes) {
      dispatch(getDocumentTypes(() => {}));
    }
  }, [hasMaster, dispatch, documentTypes]);

  useEffect(() => {
    if (hasMaster && !docTypeSums) {
      dispatch(getDocTypeSums());
    }
  }, [hasMaster, dispatch, docTypeSums]);

  if (!hasMaster) {
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );
  }

  const docTypes = documentTypes?.document_types || [];

  // Build all pills: two per doc type (positive + negative)
  const allPills = [];
  docTypes.forEach((dt) => {
    allPills.push({ key: `p${dt}`, label: `${dt} +`, sign: "positive", docType: dt });
    allPills.push({ key: `n${dt}`, label: `${dt} -`, sign: "negative", docType: dt });
  });

  // Build reverse map: pillKey -> category
  const pillToCategory = {};
  Object.entries(normMap).forEach(([docType, mapping]) => {
    if (mapping.positive) pillToCategory[`p${docType}`] = mapping.positive;
    if (mapping.negative) pillToCategory[`n${docType}`] = mapping.negative;
  });

  // Group pills by category for the pads
  const categoryPills = {};
  CATEGORIES.forEach((cat) => {
    categoryPills[cat] = allPills.filter(
      (pill) => pillToCategory[pill.key] === cat
    );
  });

  const placedKeys = new Set(Object.keys(pillToCategory));

  const saveMap = (updated) => {
    setNormMapLocal(updated);
    dispatch({ type: "SET_NORM_MAP", normMap: updated });
    dispatch(saveProject());
  };

  const handleDrop = (category, pillKey) => {
    // find which doc type and sign this pill represents
    const pill = allPills.find((p) => p.key === pillKey);
    if (!pill) return;

    // remove from any previous category
    const updated = { ...normMap };
    // ensure doc type entry exists
    if (!updated[pill.docType]) updated[pill.docType] = {};
    updated[pill.docType] = { ...updated[pill.docType], [pill.sign]: category };

    saveMap(updated);
  };

  const handleRemove = (pillKey) => {
    const pill = allPills.find((p) => p.key === pillKey);
    if (!pill) return;

    const updated = { ...normMap };
    if (updated[pill.docType]) {
      updated[pill.docType] = { ...updated[pill.docType] };
      delete updated[pill.docType][pill.sign];
      if (!updated[pill.docType].positive && !updated[pill.docType].negative) {
        delete updated[pill.docType];
      }
    }
    saveMap(updated);
  };

  return (
    <div>
      {/* Pills section */}
      <div className="box">
        <div style={{ marginBottom: "0.5rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <span
              style={{
                fontWeight: 700,
                fontSize: "0.8rem",
                color: "var(--ev-text)",
              }}
            >
              Document Types
            </span>
            <span
              style={{
                fontSize: "0.72rem",
                color: "var(--ev-text-muted)",
                marginLeft: "0.5rem",
              }}
            >
              Drag each pill into a category below
            </span>
          </div>
          <TemplateManager
            templateType="normMap"
            getCurrentData={() => normMap}
            label="Document Type Mappings"
          />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.15rem" }}>
          {allPills.map((pill) => (
            <Pill
              key={pill.key}
              label={pill.label}
              pillKey={pill.key}
              isPlaced={placedKeys.has(pill.key)}
              isPositive={pill.sign === "positive"}
              docType={pill.docType}
              onDragStart={() => {}}
            />
          ))}
        </div>
        {allPills.length === 0 && (
          <div className="notification is-info is-light">
            No document types found.
          </div>
        )}
      </div>

      {/* Bottom: Category pads + Document Type Summary side by side */}
      {allPills.length > 0 && (
        <div style={{ display: "flex", gap: "1rem", marginTop: "0.75rem", alignItems: "flex-start" }}>
          {/* Left: Category pads */}
          <div style={{ flex: "3 1 0", display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
            {CATEGORIES.map((cat) => (
              <CategoryPad
                key={cat}
                category={cat}
                pills={categoryPills[cat]}
                onDrop={handleDrop}
                onRemove={handleRemove}
              />
            ))}
          </div>

          {/* Right: Document Type Summary with tabs */}
          <div style={{ flex: "1 1 280px", minWidth: "280px" }}>
            <div style={{
              display: "flex", alignItems: "center", gap: "0",
              borderBottom: "2px solid var(--ev-border)", marginBottom: "0.5rem",
            }}>
              <button
                onClick={() => setSummaryTab("amounts")}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  padding: "0.5rem 0.75rem", fontSize: "0.8rem", fontWeight: 700,
                  color: summaryTab === "amounts" ? "var(--ev-purple)" : "var(--ev-text-muted)",
                  borderBottom: summaryTab === "amounts" ? "2px solid var(--ev-purple)" : "2px solid transparent",
                  marginBottom: "-2px", display: "flex", alignItems: "center", gap: "0.4rem",
                }}
              >
                <i className="fa-duotone fa-chart-simple" style={{ fontSize: "0.85rem" }} />
                Amount by Type
              </button>
              <button
                onClick={() => setSummaryTab("descriptions")}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  padding: "0.5rem 0.75rem", fontSize: "0.8rem", fontWeight: 700,
                  color: summaryTab === "descriptions" ? "var(--ev-purple)" : "var(--ev-text-muted)",
                  borderBottom: summaryTab === "descriptions" ? "2px solid var(--ev-purple)" : "2px solid transparent",
                  marginBottom: "-2px", display: "flex", alignItems: "center", gap: "0.4rem",
                }}
              >
                <i className="fa-duotone fa-file-lines" style={{ fontSize: "0.85rem" }} />
                Descriptions by Type
              </button>
            </div>

            {summaryTab === "amounts" && (
              docTypeSums ? (
                <table className="table is-fullwidth is-striped" style={{ fontSize: "0.78rem" }}>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th style={{ textAlign: "right" }}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          width: "14px", height: "14px", borderRadius: "3px", marginRight: "0.25rem",
                          backgroundColor: "#3DB887", color: "#fff", fontSize: "0.6rem", fontWeight: 800,
                        }}>+</span>
                        Positive
                      </th>
                      <th style={{ textAlign: "right" }}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          width: "14px", height: "14px", borderRadius: "3px", marginRight: "0.25rem",
                          backgroundColor: "#D64545", color: "#fff", fontSize: "0.6rem", fontWeight: 800,
                        }}>-</span>
                        Negative
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {docTypeSums.map((row, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{row.document_type}</td>
                        <td style={{ textAlign: "right", color: row.positive > 0 ? "var(--ev-text)" : "var(--ev-text-muted)" }}>
                          {row.positive !== 0
                            ? new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(row.positive))
                            : "—"}
                        </td>
                        <td style={{ textAlign: "right", color: row.negative < 0 ? "var(--ev-text)" : "var(--ev-text-muted)" }}>
                          {row.negative !== 0
                            ? new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(row.negative))
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ fontSize: "0.75rem", color: "var(--ev-text-muted)", fontStyle: "italic", padding: "0.5rem 0" }}>
                  Loading...
                </div>
              )
            )}

            {summaryTab === "descriptions" && (
              docTypeDescriptions && docTypeDescriptions.length > 0 ? (
                <table className="table is-fullwidth is-striped" style={{ fontSize: "0.78rem" }}>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>
                        <span style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          width: "14px", height: "14px", borderRadius: "3px", marginRight: "0.25rem",
                          backgroundColor: "#3DB887", color: "#fff", fontSize: "0.6rem", fontWeight: 800,
                        }}>+</span>
                        Positive
                      </th>
                      <th>
                        <span style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          width: "14px", height: "14px", borderRadius: "3px", marginRight: "0.25rem",
                          backgroundColor: "#D64545", color: "#fff", fontSize: "0.6rem", fontWeight: 800,
                        }}>-</span>
                        Negative
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {docTypeDescriptions.map((row, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600, verticalAlign: "top" }}>{row.document_type}</td>
                        <td style={{ verticalAlign: "top" }}>
                          {(row.positive_descriptions || []).length > 0 ? (
                            row.positive_descriptions.map((desc, j) => (
                              <div key={j} style={{ padding: "0.1rem 0", color: "var(--ev-text)" }}>
                                {desc}
                              </div>
                            ))
                          ) : (
                            <span style={{ color: "var(--ev-text-muted)", fontStyle: "italic" }}>—</span>
                          )}
                        </td>
                        <td style={{ verticalAlign: "top" }}>
                          {(row.negative_descriptions || []).length > 0 ? (
                            row.negative_descriptions.map((desc, j) => (
                              <div key={j} style={{ padding: "0.1rem 0", color: "var(--ev-text)" }}>
                                {desc}
                              </div>
                            ))
                          ) : (
                            <span style={{ color: "var(--ev-text-muted)", fontStyle: "italic" }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ fontSize: "0.75rem", color: "var(--ev-text-muted)", fontStyle: "italic", padding: "0.5rem 0" }}>
                  {docTypeSums ? "No description data available" : "Loading..."}
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* AI Prediction Section */}
      {allPills.length > 0 && (
        <div className="box" style={{ marginTop: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <i className="fa-duotone fa-wand-magic-sparkles" style={{ color: "var(--ev-purple)", fontSize: "0.9rem" }} />
              <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>AI Predicted Categorisation</span>
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              {predictedNormMap && Object.keys(predictedNormMap).length > 0 && (
                <button
                  className="button is-small"
                  onClick={() => {
                    saveMap({ ...predictedNormMap });
                  }}
                  style={{ borderRadius: "6px" }}
                >
                  <span className="icon" style={{ fontSize: "0.75rem" }}>
                    <i className="fa-duotone fa-check-double" />
                  </span>
                  <span>Accept All Predictions</span>
                </button>
              )}
              <button
                className={`button is-small ${predicting ? "is-loading" : ""}`}
                onClick={() => {
                  setPredicting(true);
                  dispatch(predictCategories(() => setPredicting(false)));
                }}
                disabled={predicting}
                style={{
                  borderRadius: "6px",
                  background: "linear-gradient(135deg, #430098, #79589F)",
                  color: "#fff",
                  border: "none",
                }}
              >
                <span className="icon" style={{ fontSize: "0.75rem" }}>
                  <i className="fa-duotone fa-wand-magic-sparkles" />
                </span>
                <span>{predictedNormMap ? "Re-predict" : "Predict"}</span>
              </button>
            </div>
          </div>

          {predictedNormMap && Object.keys(predictedNormMap).length > 0 ? (
            <div>
              {/* Side-by-side: Manual vs Predicted category pads */}
              <div style={{ display: "flex", gap: "1.5rem" }}>
                {/* Manual */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: "0.72rem", fontWeight: 700, color: "var(--ev-text-muted)",
                    textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.5rem",
                  }}>
                    Manual Assignment
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                    {CATEGORIES.filter(c => c !== "Write-Off").map((cat) => {
                      const catStyle = CATEGORY_COLORS[cat] || { bg: "#ccc", color: "#333" };
                      const pills = allPills.filter((p) => pillToCategory[p.key] === cat);
                      return (
                        <div key={cat} style={{
                          flex: "1 1 0", minWidth: "130px",
                          borderRadius: "var(--ev-radius-lg)",
                          border: "2px dashed var(--ev-border)",
                          backgroundColor: "var(--ev-white)",
                          padding: "0.5rem",
                        }}>
                          <span className="tag" style={{
                            backgroundColor: catStyle.bg, color: catStyle.color,
                            fontWeight: 700, fontSize: "0.65rem", borderRadius: "5px",
                            marginBottom: "0.35rem",
                          }}>{cat}</span>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.2rem", marginTop: "0.25rem" }}>
                            {pills.map((pill) => (
                              <PredictionPill key={pill.key} docType={pill.docType} isPositive={pill.sign === "positive"} />
                            ))}
                            {pills.length === 0 && (
                              <span style={{ fontSize: "0.65rem", color: "var(--ev-text-muted)", fontStyle: "italic" }}>—</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Predicted */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: "0.72rem", fontWeight: 700, color: "var(--ev-purple)",
                    textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.5rem",
                  }}>
                    <i className="fa-duotone fa-wand-magic-sparkles" style={{ marginRight: "0.3rem" }} />
                    AI Prediction
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                    {CATEGORIES.filter(c => c !== "Write-Off").map((cat) => {
                      const catStyle = CATEGORY_COLORS[cat] || { bg: "#ccc", color: "#333" };
                      // Build predicted pills for this category
                      const predPills = [];
                      Object.entries(predictedNormMap).forEach(([dt, mapping]) => {
                        if (mapping.positive === cat) predPills.push({ key: `p${dt}`, docType: dt, sign: "positive" });
                        if (mapping.negative === cat) predPills.push({ key: `n${dt}`, docType: dt, sign: "negative" });
                      });
                      // Check for mismatches
                      return (
                        <div key={cat} style={{
                          flex: "1 1 0", minWidth: "130px",
                          borderRadius: "var(--ev-radius-lg)",
                          border: "2px dashed var(--ev-border)",
                          backgroundColor: "var(--ev-white)",
                          padding: "0.5rem",
                        }}>
                          <span className="tag" style={{
                            backgroundColor: catStyle.bg, color: catStyle.color,
                            fontWeight: 700, fontSize: "0.65rem", borderRadius: "5px",
                            marginBottom: "0.35rem",
                          }}>{cat}</span>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.2rem", marginTop: "0.25rem" }}>
                            {predPills.map((pill) => {
                              const manualCat = pillToCategory[pill.key];
                              const isMismatch = manualCat && manualCat !== cat;
                              return (
                                <PredictionPill
                                  key={pill.key}
                                  docType={pill.docType}
                                  isPositive={pill.sign === "positive"}
                                  highlight={isMismatch}
                                />
                              );
                            })}
                            {predPills.length === 0 && (
                              <span style={{ fontSize: "0.65rem", color: "var(--ev-text-muted)", fontStyle: "italic" }}>—</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: "0.78rem", color: "var(--ev-text-muted)", fontStyle: "italic" }}>
              Click "Predict" to generate AI-powered category suggestions based on document types, descriptions, and amounts.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Read-only pill for the prediction comparison view
const PredictionPill = ({ docType, isPositive, highlight }) => (
  <span
    className="tag"
    style={{
      fontWeight: 600,
      fontSize: "0.68rem",
      borderRadius: "5px",
      backgroundColor: highlight ? "rgba(214,69,69,0.15)" : "var(--ev-white)",
      color: "var(--ev-text)",
      border: highlight ? "1px solid rgba(214,69,69,0.4)" : "1px solid var(--ev-border)",
      display: "inline-flex",
      alignItems: "center",
      gap: "0.2rem",
      padding: "0.25em 0.5em",
    }}
  >
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "13px",
        height: "13px",
        borderRadius: "3px",
        fontSize: "0.6rem",
        fontWeight: 800,
        backgroundColor: isPositive ? "#3DB887" : "#D64545",
        color: "#fff",
        lineHeight: 1,
      }}
    >
      {isPositive ? "+" : "-"}
    </span>
    {docType}
  </span>
);

export default DocumentTypes;
